import React, { useState } from 'react';
import { Baby, CheckCircle2, Circle, Activity, ChevronRight, Sparkles } from 'lucide-react';

export const EarlyInterventionModule: React.FC = () => {
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<'0_6' | '6_12' | '12_24' | '24_36'>('12_24');
  const [milestones, setMilestones] = useState({
    m1: true,
    m2: true,
    m3: false,
    m4: false
  });

  const ageGroups = [
    { id: '0_6', label: '0 - 6 Months' },
    { id: '6_12', label: '6 - 12 Months' },
    { id: '12_24', label: '12 - 24 Months' },
    { id: '24_36', label: '24 - 36 Months' },
  ];

  const items = [
    { id: 'm1', title: 'Responds to own name when called', domain: 'Auditory & Social' },
    { id: 'm2', title: 'Walks independently without support', domain: 'Gross Motor' },
    { id: 'm3', title: 'Uses 10-15 meaningful words or signs', domain: 'Speech & Language' },
    { id: 'm4', title: 'Pincer grasp to pick small objects', domain: 'Fine Motor' },
  ];

  const toggleMilestone = (id: string) => {
    setMilestones((prev: any) => ({ ...prev, [id]: !prev[id] }));
  };

  const achievedCount = Object.values(milestones).filter(Boolean).length;
  const progressPct = Math.round((achievedCount / items.length) * 100);

  return (
    <div className="p-6 space-y-6 bg-slate-900 text-white rounded-2xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/20 text-amber-300 text-xs font-bold rounded-full border border-amber-500/30">
            <Baby className="w-3.5 h-3.5" /> Module 19: Early Intervention Milestone Tracker
          </div>
          <h2 className="text-2xl font-black mt-2">Pediatric Developmental Milestone Evaluation (0-36 Months)</h2>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black text-emerald-400">{progressPct}% Achieved</div>
          <div className="text-xs text-slate-400">{achievedCount} of {items.length} Milestones</div>
        </div>
      </div>

      {/* Age Group Switcher */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {ageGroups.map((grp) => (
          <button
            key={grp.id}
            onClick={() => setSelectedAgeGroup(grp.id as any)}
            className={`p-3 rounded-xl border text-xs font-bold transition ${
              selectedAgeGroup === grp.id ? 'bg-amber-500 text-slate-950 border-amber-400 shadow' : 'bg-slate-950 text-slate-400 border-slate-800'
            }`}
          >
            {grp.label}
          </button>
        ))}
      </div>

      {/* Checklist */}
      <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-3">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Developmental Milestone Checklist:</h3>
        {items.map((item) => {
          const isDone = (milestones as any)[item.id];
          return (
            <div 
              key={item.id}
              onClick={() => toggleMilestone(item.id)}
              className={`p-3.5 rounded-xl border cursor-pointer transition flex items-center justify-between ${
                isDone ? 'bg-emerald-950/40 border-emerald-600 text-emerald-100' : 'bg-slate-900 border-slate-800 text-slate-300 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center gap-3">
                {isDone ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" /> : <Circle className="w-5 h-5 text-slate-600 shrink-0" />}
                <div>
                  <div className="text-xs font-bold">{item.title}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{item.domain}</div>
                </div>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${isDone ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-800 text-slate-400'}`}>
                {isDone ? 'MASTERED' : 'IN PROGRESS'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
